import type { Span } from '@opentelemetry/api';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { resourceFromAttributes } from '@opentelemetry/resources';
import { BatchSpanProcessor, WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { ATTR_SERVICE_NAME, ATTR_SERVICE_VERSION } from '@opentelemetry/semantic-conventions';

import { escapeRegExp } from './escapeRegExp';
import staticConfig from './staticConfig';

interface GraphqlRequestBody {
  operationName?: string;
  query?: string;
}

const originOf = (url: string): string => new URL(url, window.location.origin).origin;

/**
 * Renames an outgoing request span after the GraphQL operation it carries.
 *
 * Cloud Trace groups spans by name, and the fetch instrumentation names every span after the
 * HTTP method alone, so without this every frontend span is grouped under "POST".
 * @param span - Span created for the outgoing request
 * @param request - Request whose body carries the GraphQL operation
 */
export const nameSpanAfterGraphqlOperation = (span: Span, request: Request | RequestInit): void => {
  const { body } = request;

  if (typeof body !== 'string') {
    return;
  }

  try {
    const { operationName, query } = JSON.parse(body) as GraphqlRequestBody;

    if (!operationName) {
      return;
    }

    const operationType: string = /^\s*(query|mutation|subscription)\b/.exec(query ?? '')?.[1] ?? 'query';

    span.updateName(`${operationType} ${operationName}`);
    span.setAttribute('graphql.operation.name', operationName);
    span.setAttribute('graphql.operation.type', operationType);
  } catch {
    // A body that is not JSON is not a GraphQL operation, so the default span name stands.
  }
};

/**
 * Starts browser tracing so a user action is recorded as one trace through to the backend.
 *
 * Spans are exported to a same-origin path that Istio routes to the collector, so telemetry
 * needs no CORS and offers no third-party host for ad blockers to match. Returns without
 * starting anything when `otlpTracesUrl` is unset, which is how tracing stays off per environment.
 */
export const initTracing = (): void => {
  const { applicationVersion, environmentName, otlpTracesUrl, unproxiedGraphqlUrl } = staticConfig;

  if (!otlpTracesUrl) {
    return;
  }

  const provider: WebTracerProvider = new WebTracerProvider({
    resource: resourceFromAttributes({
      [ATTR_SERVICE_NAME]: 'frontend',
      [ATTR_SERVICE_VERSION]: applicationVersion,
      'deployment.environment': environmentName
    }),
    spanProcessors: [new BatchSpanProcessor(new OTLPTraceExporter({ url: otlpTracesUrl }))]
  });

  provider.register();

  registerInstrumentations({
    instrumentations: [
      new DocumentLoadInstrumentation(),
      new FetchInstrumentation({
        // Backstop only: the exporter already calls the unpatched fetch, so its own requests
        // do not become spans. Without either guard, exporting would slowly feed itself.
        ignoreUrls: [new RegExp(escapeRegExp(otlpTracesUrl))],
        // GraphQL is on a different origin, so `traceparent` is attached only where the server
        // is known to accept it on a CORS preflight.
        propagateTraceHeaderCorsUrls: [new RegExp(`^${escapeRegExp(originOf(unproxiedGraphqlUrl))}`)],
        requestHook: nameSpanAfterGraphqlOperation
      })
    ]
  });

  // A tab closing or backgrounding would otherwise discard whatever the batch processor holds.
  document.addEventListener('visibilitychange', (): void => {
    if (document.visibilityState === 'hidden') {
      void provider.forceFlush();
    }
  });
};
